import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import { Button, GraphQLErrorMessage, Heading3, Input, Paragraph } from '@treams/treams-ui';

import { isHubspotRequired } from './WorkspaceCreateModal';
import { WorkspaceAdvanced } from './types';

export default function WorkspaceHubSpot(props: { workspace: WorkspaceAdvanced }) {
	const { t } = useTranslation();
	const [hubSpotCompanyID, setHubSpotCompanyID] = useState(props.workspace.hubSpotCompanyID ?? '');
	const [update, { loading, error }] = graphql.useUpdateWorkspaceHubSpotCompanyIdMutation();

	useEffect(() => {
		setHubSpotCompanyID(props.workspace.hubSpotCompanyID ?? '');
	}, [props.workspace.hubSpotCompanyID]);

	const save = useCallback(async () => {
		try {
			await update({
				variables: {
					input: {
						workspaceID: props.workspace.id,
						hubSpotCompanyID,
					},
				},
			});
		} catch {
			// Error is handled by the error state from the mutation hook
		}
	}, [update, props.workspace.id, hubSpotCompanyID]);

	const isRequired = isHubspotRequired(props.workspace.id);
	const isMissingRequired = isRequired && !hubSpotCompanyID.trim();

	return (
		<Box flexGrow={1} flexBasis={0} mt="30px">
			<Heading3 mb={2}>{t('settings.workspaces.hubspot.label')}</Heading3>
			{error && (
				<Box mb={2}>
					<GraphQLErrorMessage error={error.graphQLErrors?.[0] ?? error} />
				</Box>
			)}
			{isMissingRequired && (
				<Paragraph color="red" mb={2}>
					{t('workspaces.form.hubSpotCompanyIDRequired')}
				</Paragraph>
			)}
			<Flex>
				<Input value={hubSpotCompanyID} onChange={setHubSpotCompanyID} flexGrow={1} mr={2} />
				<Button
					onClick={save}
					small
					title={t('common.save')}
					type="primary"
					flexGrow={0}
					flexShrink={0}
					disabled={
						loading ||
						hubSpotCompanyID === (props.workspace.hubSpotCompanyID ?? '') ||
						isMissingRequired
					}
				/>
			</Flex>
		</Box>
	);
}
